import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import ArticleContentRenderer from './ArticleContentRenderer';
import RelatedArticles from './RelatedArticles';
import { articlesApi, pageContentApi } from '../../lib/api';
import { Skeleton } from '../ui/skeleton';
import { useFavoritedIds } from '../../hooks/useFavoritedIds';

// Thin, data-driven shell for the long-form "Engineering Reference Series" articles.
// Fetches its content from page_contents (admin-editable via /admin/content) and
// renders it through ArticleContentRenderer, so wording, images, and structure can
// all be changed without touching code. Existing callers hardcode `slug`/`pdfSlug` as
// props (one dedicated route per article); the generic `/articles/reference/:slug`
// route instead renders this with no props, so it falls back to the URL param —
// letting newly created Content Library articles reuse this same component.
const DataDrivenArticlePage = ({ slug: slugProp, pdfSlug: pdfSlugProp }) => {
  const { slug: slugParam } = useParams();
  const slug = slugProp || slugParam;
  // Existing hardcoded callers always pass pdfSlug explicitly (it can differ from slug,
  // e.g. slug="article-bms" / pdfSlug="bms"). The generic route passes neither, so derive
  // it the same way new Content Library articles are created: strip the "article-" prefix
  // that GET /api/articles/static/{slug}/pdf expects (see AdminContentEditorPage's
  // slugPrefixFor convention).
  const pdfSlug = pdfSlugProp || (slug ? slug.replace(/^article-/, '') : slug);
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // This component only ever renders page_contents docs with content_type "article"
  // (the "Engineering Reference Series") -- see the Favorites content_type scheme in
  // UserFavorite's docstring, backend/db/models.py.
  const favoritedIds = useFavoritedIds('page_content_article', content ? [content.page_content_id] : []);

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const data = await pageContentApi.getPageContent(slug);
        setContent(data);
      } catch (err) {
        console.error('Error fetching article content:', err);
        setError('Failed to load article');
      } finally {
        setLoading(false);
      }
    };
    fetchContent();
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-white px-4 md:px-8 py-16 max-w-screen-2xl mx-auto space-y-4" data-testid="article-loading">
        <Skeleton className="h-10 w-2/3" />
        <Skeleton className="h-4 w-1/3" />
        <Skeleton className="h-32 w-full mt-8" />
      </div>
    );
  }

  if (error || !content) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-destructive">{error || 'Article not found'}</p>
      </div>
    );
  }

  return (
    <div data-testid="data-driven-article-page">
      <ArticleContentRenderer
        content={content}
        onDownloadPdf={() => articlesApi.downloadStaticArticlePdf(pdfSlug)}
        favorite={{
          contentType: 'page_content_article',
          contentId: content.page_content_id,
          initialFavorited: favoritedIds.has(content.page_content_id),
        }}
      />
      <RelatedArticles current={content} />
    </div>
  );
};

export default DataDrivenArticlePage;
